import type { CreateProps, RaRecord } from "react-admin";
import {
  AutocompleteInput,
  Create,
  DateInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
} from "react-admin";
import { useWatch } from "react-hook-form";

import { LeaveBalanceAlert } from "./LeaveBalanceAlert";
import { LeaveRangeBreakupAlert } from "./LeaveRangeBreakupAlert";
import {
  LEAVE_TYPE_CHOICES,
  employeeNameFilterToQuery,
  transformLeaveCreatePayload,
  type EmployeeLeaveType,
  type LeaveCreateFormValues,
} from "./leave-request-helpers";

function defaultEmployeeId(): number | undefined {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("employeeId");
  if (!raw) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const employeeOptionText = (choice: RaRecord) =>
  choice?.name ? `${choice.name} (#${choice.id})` : `#${choice?.id ?? ""}`;

/** Watches create-form fields so balance/breakup update as the user fills dates. */
function LeaveCreateLiveAlerts() {
  const employeeIdRaw = useWatch({ name: "employeeId" });
  const leaveType = useWatch({ name: "leaveType" }) as EmployeeLeaveType | undefined;
  const startDate = useWatch({ name: "startDate" });
  const endDate = useWatch({ name: "endDate" });
  const employeeId = Number(employeeIdRaw);

  if (!Number.isFinite(employeeId) || employeeId < 1) {
    return null;
  }

  return (
    <>
      <LeaveBalanceAlert employeeId={employeeId} mode="office" />
      <LeaveRangeBreakupAlert
        mode="office"
        employeeId={employeeId}
        leaveType={leaveType}
        startDate={startDate}
        endDate={endDate}
      />
    </>
  );
}

const EmployeeLeaveRequestCreate = (props: CreateProps) => {
  const employeeId = defaultEmployeeId();

  return (
    <Create
      {...props}
      redirect="show"
      transform={(data) => transformLeaveCreatePayload(data as LeaveCreateFormValues)}
    >
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 560 }} defaultValues={{ employeeId }}>
        <ReferenceInput source="employeeId" reference="employees" perPage={50}>
          <AutocompleteInput
            optionText={employeeOptionText}
            label="Employee"
            validate={required()}
            fullWidth
            filterToQuery={employeeNameFilterToQuery}
          />
        </ReferenceInput>
        <SelectInput
          source="leaveType"
          label="Leave type"
          choices={LEAVE_TYPE_CHOICES}
          validate={required()}
          fullWidth
        />
        <DateInput source="startDate" label="Start date" validate={required()} fullWidth />
        <DateInput source="endDate" label="End date" validate={required()} fullWidth />
        <LeaveCreateLiveAlerts />
        <TextInput source="reason" label="Reason" multiline minRows={3} fullWidth resettable />
      </SimpleForm>
    </Create>
  );
};

export default EmployeeLeaveRequestCreate;
